import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, ChevronDown, ChevronUp, Loader2, Play, Plus, Save, Trash2, X } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import FlowMap from "./FlowMap";
import EntityPortal from "./EntityPortal";

export function StoryPlayer({ story, graph, layers, onExit }) {
    const [idx, setIdx] = useState(0);
    const step = story.steps[idx];
    const last = idx === story.steps.length - 1;

    return (
        <div className="fixed inset-0 z-50 bg-abyss flex flex-col" data-testid="story-player">
            <header className="h-14 shrink-0 border-b border-border bg-canvas/90 backdrop-blur flex items-center gap-3 px-4">
                <span className="font-display font-bold text-slate-100 truncate">{story.title}</span>
                <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500 shrink-0">
                    Steg {idx + 1} / {story.steps.length}
                </span>
                <div className="flex-1" />
                <button data-testid="story-prev" onClick={() => setIdx((i) => Math.max(0, i - 1))} disabled={idx === 0}
                    className="flex items-center gap-1.5 text-xs text-slate-300 border border-border rounded-lg px-3 py-1.5 hover:border-current/50 hover:text-current transition-colors disabled:opacity-40">
                    <ArrowLeft className="w-3.5 h-3.5" /> Föregående
                </button>
                <button data-testid="story-next" onClick={() => (last ? onExit() : setIdx((i) => i + 1))}
                    className="flex items-center gap-1.5 text-xs font-semibold bg-current text-abyss rounded-lg px-3 py-1.5 hover:bg-tide transition-colors">
                    {last ? "Avsluta" : "Nästa"} <ArrowRight className="w-3.5 h-3.5" />
                </button>
                <button data-testid="story-exit" onClick={onExit}
                    className="text-slate-500 hover:text-slate-200 transition-colors">
                    <X className="w-4 h-4" />
                </button>
            </header>
            {step.notering && (
                <div className="shrink-0 px-4 py-2 border-b border-border bg-deepsea/40" data-testid="story-note">
                    <p className="text-xs text-slate-300 italic leading-relaxed">"{step.notering}"</p>
                </div>
            )}
            <div className="flex-1 min-h-0 relative">
                {step.kind === "entitet" && step.node_id ? (
                    <div className="absolute inset-0 overflow-y-auto panel-scroll">
                        <div className="max-w-2xl mx-auto py-6 flex flex-col min-h-full">
                            <EntityPortal nodeId={step.node_id} inline />
                        </div>
                    </div>
                ) : (
                    <FlowMap graph={graph} layers={layers} presentation />
                )}
            </div>
        </div>
    );
}

export default function StoryPanel({ projectId, graph, prefill, onPrefillConsumed, onPlay, onClose }) {
    const [stories, setStories] = useState([]);
    const [current, setCurrent] = useState(null);
    const [entityPick, setEntityPick] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => api.get(`/projects/${projectId}/stories`).then((r) => setStories(r.data)).catch((e) => toast.error(formatApiError(e)));
    useEffect(() => { load(); }, [projectId]);

    useEffect(() => {
        if (!prefill) return;
        setCurrent((prev) => {
            const base = prev || { id: null, title: "Ny berättelse", steps: [] };
            return { ...base, steps: [...base.steps, prefill] };
        });
        onPrefillConsumed();
    }, [prefill]);

    const nodeLabel = (id) => graph.nodes.find((n) => n.id === id)?.label || "Okänd";

    const move = (i, dir) => {
        const steps = [...current.steps];
        const j = i + dir;
        if (j < 0 || j >= steps.length) return;
        [steps[i], steps[j]] = [steps[j], steps[i]];
        setCurrent({ ...current, steps });
    };

    const save = async () => {
        if (!current) return;
        setSaving(true);
        try {
            const payload = { title: current.title, steps: current.steps };
            const r = current.id
                ? await api.put(`/stories/${current.id}`, payload)
                : await api.post(`/projects/${projectId}/stories`, payload);
            setCurrent(r.data);
            await load();
            toast.success("Berättelsen sparad");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    };

    const remove = async (sid) => {
        try {
            await api.delete(`/stories/${sid}`);
            if (current?.id === sid) setCurrent(null);
            await load();
            toast.success("Berättelsen borttagen");
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-abyss/80 backdrop-blur-sm p-4" data-testid="story-panel">
            <div className="w-full max-w-xl max-h-[85vh] flex flex-col rounded-2xl border border-border bg-canvas shadow-2xl">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                    <h2 className="font-display font-bold text-lg text-slate-100">Berättelser</h2>
                    <button data-testid="story-panel-close" onClick={onClose} className="text-slate-600 hover:text-slate-300 transition-colors">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto panel-scroll p-4 space-y-4">
                    {stories.length > 0 && (
                        <div className="space-y-1.5">
                            {stories.map((s) => (
                                <div key={s.id} data-testid={`story-item-${s.id}`}
                                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${current?.id === s.id ? "border-current/50 bg-current/10" : "border-border hover:bg-deepsea/50"}`}
                                    onClick={() => setCurrent({ id: s.id, title: s.title, steps: s.steps })}>
                                    <span className="flex-1 text-sm text-slate-200 truncate">{s.title}</span>
                                    <span className="font-mono text-[9px] text-slate-500">{s.steps.length} steg</span>
                                    <button data-testid={`story-play-${s.id}`} title="Spela upp" disabled={!s.steps.length}
                                        onClick={(e) => { e.stopPropagation(); onPlay(s); }}
                                        className="text-buoy hover:text-emerald-300 transition-colors disabled:opacity-40">
                                        <Play className="w-4 h-4" />
                                    </button>
                                    <button data-testid={`story-delete-${s.id}`} title="Ta bort"
                                        onClick={(e) => { e.stopPropagation(); remove(s.id); }}
                                        className="text-slate-600 hover:text-red-400 transition-colors">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {!current ? (
                        <button data-testid="story-new-button" onClick={() => setCurrent({ id: null, title: "Ny berättelse", steps: [] })}
                            className="w-full flex items-center justify-center gap-2 rounded-lg bg-current text-abyss px-3 py-2 text-xs font-semibold hover:bg-tide transition-colors">
                            <Plus className="w-3.5 h-3.5" /> Ny berättelse
                        </button>
                    ) : (
                        <div className="space-y-3">
                            <div>
                                <label className="font-mono text-[9px] uppercase tracking-widest text-slate-500 block mb-1">Titel</label>
                                <input data-testid="story-title-input" value={current.title}
                                    onChange={(e) => setCurrent({ ...current, title: e.target.value })}
                                    className="w-full bg-abyss/60 border border-border rounded-lg px-3 py-2 text-sm text-slate-100 outline-none focus:border-current/60" />
                            </div>

                            <div className="flex gap-2">
                                <button data-testid="story-add-helhet"
                                    onClick={() => setCurrent({ ...current, steps: [...current.steps, { kind: "helhet", node_id: null, notering: "" }] })}
                                    className="flex-1 flex items-center justify-center gap-1.5 rounded-lg border border-border text-slate-300 px-2 py-2 text-xs hover:border-current/50 hover:text-current transition-colors">
                                    <Plus className="w-3.5 h-3.5" /> Helhetsbild
                                </button>
                                <select data-testid="story-entity-select" value={entityPick} onChange={(e) => setEntityPick(e.target.value)}
                                    className="flex-1 bg-abyss/60 border border-border rounded-lg px-2 py-2 text-xs text-slate-200 outline-none">
                                    <option value="">Välj entitet …</option>
                                    {graph.nodes.map((n) => <option key={n.id} value={n.id}>{n.label}</option>)}
                                </select>
                                <button data-testid="story-add-entity" disabled={!entityPick}
                                    onClick={() => { setCurrent({ ...current, steps: [...current.steps, { kind: "entitet", node_id: entityPick, notering: "" }] }); setEntityPick(""); }}
                                    className="shrink-0 rounded-lg border border-current/40 text-current px-2.5 py-2 text-xs hover:bg-current/10 transition-colors disabled:opacity-40">
                                    <Plus className="w-3.5 h-3.5" />
                                </button>
                            </div>

                            <div className="space-y-2">
                                {current.steps.length === 0 && (
                                    <p className="text-xs text-slate-600 text-center py-3">Inga steg ännu – lägg till en helhetsbild eller en entitet.</p>
                                )}
                                {current.steps.map((s, i) => (
                                    <div key={i} data-testid={`story-step-${i}`} className="rounded-lg border border-border bg-abyss/40 p-2.5 space-y-1.5">
                                        <div className="flex items-center gap-2">
                                            <span className="font-mono text-[9px] text-slate-500">{i + 1}.</span>
                                            <span className="flex-1 text-xs font-semibold text-slate-200 truncate">
                                                {s.kind === "helhet" ? "Helhetsbild" : nodeLabel(s.node_id)}
                                            </span>
                                            <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-500 hover:text-current disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
                                            <button onClick={() => move(i, 1)} disabled={i === current.steps.length - 1} className="text-slate-500 hover:text-current disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
                                            <button data-testid={`story-step-delete-${i}`} onClick={() => setCurrent({ ...current, steps: current.steps.filter((_, j) => j !== i) })}
                                                className="text-slate-600 hover:text-red-400"><Trash2 className="w-3.5 h-3.5" /></button>
                                        </div>
                                        <input data-testid={`story-step-note-${i}`} value={s.notering}
                                            onChange={(e) => setCurrent({ ...current, steps: current.steps.map((x, j) => j === i ? { ...x, notering: e.target.value } : x) })}
                                            placeholder="Notering till publiken (valfritt) …"
                                            className="w-full bg-abyss/60 border border-border rounded px-2 py-1 text-[11px] text-slate-300 outline-none focus:border-current/60" />
                                    </div>
                                ))}
                            </div>

                            <div className="flex gap-2">
                                <button data-testid="story-back-to-list" onClick={() => setCurrent(null)}
                                    className="shrink-0 rounded-lg border border-border text-slate-400 px-3 py-2 text-xs hover:border-current/50 hover:text-current transition-colors">
                                    Tillbaka
                                </button>
                                <button data-testid="story-save-button" onClick={save} disabled={saving || !current.steps.length}
                                    className="flex-1 flex items-center justify-center gap-2 rounded-lg bg-current text-abyss px-3 py-2 text-xs font-semibold hover:bg-tide transition-colors disabled:opacity-50">
                                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Spara
                                </button>
                                <button data-testid="story-play-current" onClick={() => onPlay(current)} disabled={!current.steps.length}
                                    className="flex-1 flex items-center justify-center gap-2 rounded-lg border border-buoy/40 bg-buoy/10 text-buoy px-3 py-2 text-xs font-semibold hover:bg-buoy/20 transition-colors disabled:opacity-50">
                                    <Play className="w-3.5 h-3.5" /> Spela upp
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
